import { useContext, useMemo } from 'react'
import UUIThemeContext from '../theme/context'

import { UUIButtonProps } from '../types/UUIButton'

export default function UUIButton({
  uiLeftIcon,
  uiRightIcon,
  uiVariant = 'normal',
  uiSize = 'normal',
  uiStyle = 'darkBlue',
  uiLoading = false,
  className,
  children,
  href,
  ...HTMLProps
}: UUIButtonProps) {
  const theme = useContext(UUIThemeContext)

  const themeClassNames = useMemo(() => {
    const variant = uiVariant
    const size = uiSize
    const style = uiStyle

    const element =
      'function' === typeof theme?.Button?.element
        ? theme.Button.element({ style, size, variant })
        : 'object' === typeof theme?.Button?.element
          ? theme.Button.element[style]?.[size]?.[variant] || ''
          : ''
    const label =
      'function' === typeof theme?.Button?.label
        ? theme.Button.label({ style, size, variant })
        : 'object' === typeof theme?.Button?.label
          ? theme.Button.label[style]?.[size]?.[variant] || ''
          : ''

    const leftIcon =
      'function' === typeof theme?.Button?.leftIcon
        ? theme.Button.leftIcon({ style, size, variant })
        : 'object' === typeof theme?.Button?.leftIcon
          ? theme.Button.leftIcon[style]?.[size]?.[variant] || ''
          : ''
    const rightIcon =
      'function' === typeof theme?.Button?.rightIcon
        ? theme.Button.rightIcon({ style, size, variant })
        : 'object' === typeof theme?.Button?.rightIcon
          ? theme.Button.rightIcon[style]?.[size]?.[variant] || ''
          : ''

    return { element, label, leftIcon, rightIcon }
  }, [theme, uiVariant, uiSize, uiStyle])

  // An `href` makes this a link wearing the button skin — keeps middle-click,
  // "open in new tab" and the link semantics a navigation action needs.
  const Element = (href ? 'a' : 'button') as React.ElementType

  return (
    <Element
      className={`tw:relative ${themeClassNames.element} ${className || ''} ${!uiLeftIcon && 'tw:pr-3'} ${
        !uiRightIcon && 'tw:pl-3'
      }`}
      href={href}
      {...HTMLProps}
      disabled={uiLoading || HTMLProps.disabled}
    >
      {uiLeftIcon && (
        <span className={`${themeClassNames.leftIcon} ${uiLoading ? 'tw:opacity-30' : ''}`}>
          <i className={`${uiLeftIcon}`} />
        </span>
      )}
      <span className={`${themeClassNames.label} ${uiLoading ? 'tw:opacity-30' : ''}`}>{children}</span>
      {uiRightIcon && (
        <span className={`${themeClassNames.rightIcon} ${uiLoading ? 'tw:opacity-30' : ''}`}>
          <i className={` ${uiRightIcon}`} />
        </span>
      )}
      {uiLoading && (
        <span className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
          <i className={`icon-aytmfd-spin afd-spin ${'normal' === uiSize ? 'tw:text-lg' : 'tw:text-sm'}`} />
        </span>
      )}
    </Element>
  )
}
